import { cloneElement, useState } from 'react'
import { Ico } from '@/ui/Ico'
import { cls } from '@/ui/cls'
import { Eyebrow } from '@/ui'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { outcomeNextStep, type OutcomeKind } from '@/shared/domain'
import { useCanEdit, writeControlProps } from '@/shared/write-access'

// Visual styling per action. Variants differ only in colour weight — never in
// size — so the action bar reads as one consistent rhythm. Tone groups:
// primary outcomes (Reached / No answer / Email sent / Booked) carry full
// label weight; closing dispositions (Not a fit / Wrong contact / Already
// customer) and Postpone use the same shape with a quieter colour palette so
// the eye lands on the engagement row first without anything looking
// shrunken or out of place.
//
// Kontakta ej sits beside the closing dispositions but is not an outcome: it
// logs no call, it posts the do-not-contact mark (see DoNotContactSheet).
type BarKind = OutcomeKind | 'do_not_contact'

const AB_VARIANT_CLS: Record<BarKind, { btn: string; icon: string; label: string }> = {
  reached:     { btn: '',                               icon: 'bg-ink-100 text-ink-1000', label: 'text-ink-1000 font-semibold' },
  referred:    { btn: '',                               icon: 'bg-ink-100 text-ink-1000', label: 'text-ink-1000 font-semibold' },
  voicemail:   { btn: '',                               icon: 'bg-ink-100 text-ink-700',  label: 'text-ink-1000 font-semibold' },
  email_sent:  { btn: '',                               icon: 'bg-ink-100 text-ink-700',  label: 'text-ink-1000 font-semibold' },
  booked:      { btn: 'hover:!border-brand-deep', icon: 'bg-ink-100 text-brand-deep', label: 'text-ink-1000 font-semibold' },
  notfit:      { btn: '',                               icon: 'bg-ink-50 text-ink-500',   label: 'text-ink-700 font-medium' },
  bad_contact: { btn: '',                               icon: 'bg-ink-50 text-ink-500',   label: 'text-ink-700 font-medium' },
  customer:    { btn: '',                               icon: 'bg-ink-50 text-ink-500',   label: 'text-ink-700 font-medium' },
  do_not_contact: { btn: '',                            icon: 'bg-ink-50 text-[var(--color-danger)]', label: 'text-ink-700 font-medium' },
}

// Short display labels — distinct from OUTCOMES[kind].label, which is tuned
// for the outcome sheet's title ("Booked meeting · Acme AB") rather than a
// button that already has an icon doing some of that work.
const SHORT_LABEL: Record<BarKind, string> = {
  reached: 'Kontakt',
  referred: 'Hänvisad',
  voicemail: 'Inget svar',
  email_sent: 'Mejl skickat',
  booked: 'Bokat',
  notfit: 'Ej aktuell',
  bad_contact: 'Fel kontakt',
  customer: 'Redan kund',
  do_not_contact: 'Kontakta ej',
}

const nextStep = (kind: BarKind): string =>
  kind === 'do_not_contact' ? 'kontakten vill inte bli kontaktad' : outcomeNextStep(kind)

function ActionButton({
  kind, kbd, icon, onClick, canEdit,
}: {
  kind: BarKind
  // Absent when the outcome has no keyboard binding. The key no longer shows
  // as a chip (YCRM-20 hides the hints while the shortcuts stay live) — when
  // present it only folds into the hover tooltip.
  kbd?: string
  icon: React.ReactElement<{ width?: number; height?: number }>
  onClick: () => void
  canEdit: boolean
}) {
  const v = AB_VARIANT_CLS[kind]
  const sizedIcon = cloneElement(icon, { width: 11, height: 11 })
  return (
    <button
      type="button"
      onClick={onClick}
      {...writeControlProps(canEdit, `${SHORT_LABEL[kind]}${kbd ? ` (${kbd})` : ''} — ${nextStep(kind)}`)}
      className={cls(
        'grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto] items-center gap-1.5',
        'px-2 py-1 bg-white border border-ink-200 rounded-none cursor-pointer text-left font-[inherit]',
        'hover:border-ink-700 transition-colors w-full',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-ink-200',
        v.btn,
      )}
    >
      <span className={cls('w-5 h-5 grid place-items-center rounded-full font-semibold leading-none shrink-0', v.icon)}>
        {sizedIcon}
      </span>
      {/* Name and "what happens next" stack on their own lines rather than
          joining on one — "Already customer · closes the lead" has nowhere
          near enough room on a single line at 5-across, even at 1440 with
          the activity panel open, and the next-step text is the point of
          the button, so it has to actually be legible rather than quietly
          truncate into "...clo…". Only the name truncates; the next-step
          line wraps instead, so it stays whole rather than clipping. */}
      <span className="min-w-0 flex flex-col leading-tight">
        <span className={cls('text-xs truncate', v.label)}>{SHORT_LABEL[kind]}</span>
        <span className="text-[10px] text-ink-500 font-normal">{nextStep(kind)}</span>
      </span>
    </button>
  )
}

// Postpone preset durations. Mirrors the OutcomeSheet's "When should we
// contact again?" short-range chips so the BDR doesn't have to learn two
// vocabularies — picking "1 week" here and "1 week" there means the same
// thing (just one logs an event and one doesn't).
const POSTPONE_PRESETS: Array<{ label: string; days: number }> = [
  { label: 'Imorgon', days: 1 },
  { label: '3 dagar', days: 3 },
  { label: '1 vecka', days: 7 },
  { label: '2 veckor', days: 14 },
  { label: '1 månad', days: 30 },
]

function PostponeButton({ onPostpone, canEdit }: {
  onPostpone: (days: number) => void
  canEdit: boolean
}) {
  const [open, setOpen] = useState(false)
  const clockIcon = cloneElement(Ico.clock, { width: 11, height: 11 })

  const pick = (days: number) => {
    setOpen(false)
    onPostpone(days)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          {...writeControlProps(canEdit, 'Skjut upp (P) — dölj den här leaden ett tag')}
          className="grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto] items-center gap-1.5
                     px-2 py-1 bg-white border border-ink-200 rounded-none cursor-pointer text-left font-[inherit]
                     hover:border-ink-700 transition-colors w-full
                     disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-ink-200"
        >
          <span className="w-5 h-5 grid place-items-center rounded-full bg-ink-50 text-ink-500 font-semibold leading-none shrink-0">
            {clockIcon}
          </span>
          <span className="text-xs text-ink-700 font-medium truncate">Skjut upp</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[180px] rounded-md p-1">
        <Eyebrow size="sm" gap="none" className="px-2 py-1">
          Dölj i…
        </Eyebrow>
        {POSTPONE_PRESETS.map(p => (
          <button
            key={p.days}
            type="button"
            onClick={() => pick(p.days)}
            className="w-full text-left text-xs text-ink-1000 px-2 py-1.5 bg-transparent border-0 rounded-sm cursor-pointer hover:bg-ink-50"
          >
            {p.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}

export interface OutcomeBarProps {
  onAction: (kind: OutcomeKind) => void
  /** Opens the referral-capture sheet — Referred needs fields a plain click can't collect. */
  onReferStart: () => void
  /** Absent hides Postpone — the overlay only wires this up once a lead is in view. */
  onPostpone?: (days: number) => void
  /** Opens the Kontakta ej sheet. Absent hides the button, for a user who may not mark. */
  onDoNotContact?: () => void
}

// The outcome action bar — same buttons, same order, same keyboard letters in
// both Focus and the lead overlay, so "press B" means the same thing
// everywhere and neither surface can drift from the other's catalogue.
export default function OutcomeBar({ onAction, onReferStart, onPostpone, onDoNotContact }: OutcomeBarProps) {
  const canEdit = useCanEdit()
  return (
    <div className="flex flex-col gap-1">
      {/* Primary row — engagement outcomes the BDR reaches for first */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-1">
        <ActionButton kind="reached"    kbd="R" icon={Ico.phone}  onClick={() => onAction('reached')} canEdit={canEdit} />
        <ActionButton kind="referred"           icon={Ico.arrowR} onClick={onReferStart} canEdit={canEdit} />
        <ActionButton kind="voicemail"  kbd="N" icon={Ico.dots}   onClick={() => onAction('voicemail')} canEdit={canEdit} />
        <ActionButton kind="email_sent" kbd="E" icon={Ico.mail}   onClick={() => onAction('email_sent')} canEdit={canEdit} />
        <ActionButton kind="booked"     kbd="B" icon={Ico.calendar} onClick={() => onAction('booked')} canEdit={canEdit} />
      </div>
      {/* Secondary row — closing dispositions + Postpone. Same shape and size
          as the primary row; the colour weight is what tells the eye these
          are the less-common path. */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-1">
        <ActionButton kind="notfit"      kbd="X" icon={Ico.x}      onClick={() => onAction('notfit')} canEdit={canEdit} />
        <ActionButton kind="bad_contact" kbd="W" icon={Ico.search} onClick={() => onAction('bad_contact')} canEdit={canEdit} />
        <ActionButton kind="customer"    kbd="C" icon={Ico.link}   onClick={() => onAction('customer')} canEdit={canEdit} />
        {onDoNotContact && <ActionButton kind="do_not_contact" icon={Ico.ban} onClick={onDoNotContact} canEdit={canEdit} />}
        {onPostpone && <PostponeButton onPostpone={onPostpone} canEdit={canEdit} />}
      </div>
    </div>
  )
}
